import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import API from "../../../services/api";
import {
  PhoneOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  UserOutlined,
  ColumnWidthOutlined,
  CarOutlined,
  TeamOutlined,
  BankOutlined,
  FireOutlined,
} from "@ant-design/icons";
import { FaWhatsapp } from "react-icons/fa";
import { Spin, Empty, message } from "antd";
import "./AdsDetails.css";

const TABS = ["Details", "Description", "Location", "Audience"];

function AdsDetails() {
  const { slug } = useParams();
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [activeTab, setActiveTab] = useState("Details");
  const [imageErrors, setImageErrors] = useState({});
  const hasTrackedView = useRef(false);
  const stripRef = useRef(null);
  const [images, setImages] = useState([]);
  const [otherImages, setOtherImages] = useState([]);

  const handleNext = useCallback(() => {
    if (otherImages.length > 0) {
      setPreviewIndex((prev) => (prev + 1) % otherImages.length);
    }
  }, [otherImages.length]);

  const handlePrev = useCallback(() => {
    if (otherImages.length > 0) {
      setPreviewIndex((prev) => (prev === 0 ? otherImages.length - 1 : prev - 1));
    }
  }, [otherImages.length]);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
  }, []);

  const handleImageError = useCallback((index, isMain = false) => {
    setImageErrors((prev) => ({ ...prev, [index]: true }));
    if (isMain && images.length > 1) {
      // Find next available image that hasn't failed
      const nextAvailableIndex = images.findIndex((_, idx) => !imageErrors[idx] && idx !== index);
      if (nextAvailableIndex !== -1) {
        setActiveImage(nextAvailableIndex);
      }
    }
  }, [images, imageErrors]);

  const getVisitorId = () => {
    let visitorId = localStorage.getItem("visitorId");
    if (!visitorId) {
      visitorId = `guest_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("visitorId", visitorId);
    }
    return visitorId;
  };

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    const fetchAd = async () => {
      try {
        setLoading(true);
        let res;
        try {
          res = await API.get(`/ads/slug/${slug}`);
        } catch (err) {
          try {
            res = await API.get(`/ads/${slug}`);
          } catch (error) {
            console.log("Both slug and ID fetch failed:", error);
            setAd(null);
            message.error("Failed to load advertisement");
            return;
          }
        }
        setAd(res.data);

        if (res.data?._id && !hasTrackedView.current) {
          hasTrackedView.current = true;
          try {
            const visitorId = getVisitorId();
            const viewRes = await API.post(
              `/ads/${res.data._id}/view`,
              {},
              { headers: { "x-visitor-id": visitorId } }
            );
            setAd((prev) =>
              prev
                ? {
                  ...prev,
                  views:
                    typeof viewRes.data.views === "number"
                      ? viewRes.data.views
                      : prev.views,
                }
                : prev
            );
          } catch (viewError) {
            console.log("View count error:", viewError);
          }
        }
      } catch (err) {
        console.log(err);
        setAd(null);
        message.error("Error loading advertisement details");
      } finally {
        setLoading(false);
      }
    };

    hasTrackedView.current = false;
    setActiveImage(0);
    fetchAd();
  }, [slug]);

  useEffect(() => {
    if (previewOpen && stripRef.current) {
      const activeThumb = stripRef.current.children[previewIndex];
      if (activeThumb) {
        activeThumb.scrollIntoView({ block: "nearest", inline: "center" });
      }
    }
  }, [previewIndex, previewOpen]);

  useEffect(() => {
    if (previewOpen) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, [previewOpen]);

  useEffect(() => {
    if (ad) {
      const thumbnailOnly = ad.thumbnail ? [ad.thumbnail] : [];
      const otherImagesList = (ad.images || []).filter((img) => img !== ad.thumbnail);
      if (otherImagesList.length > 0) {
        setImages([otherImagesList[0]]);
        setOtherImages(otherImagesList);
      } else {
        setImages(thumbnailOnly);
        setOtherImages([]);
      }
      setImageErrors({});
      setActiveImage(0);
    }
  }, [ad]);

  const getPriceLabel = (unit) => {
    if (unit === "day") return "/day";
    if (unit === "week") return "/week";
    if (unit === "month") return "/month";
    return "";
  };

  const renderDetailRow = (label, value) =>
    value !== null && value !== undefined && value !== "" ? (
      <tr key={label}>
        <td className="adv-detail-label">{label}</td>
        <td className="adv-detail-value">{value}</td>
      </tr>
    ) : null;

  const renderImageWithFallback = (src, alt, className, index, isMain = false) => {
    if (imageErrors[index]) {
      return (
        <div className={`${className} adv-image-fallback`}>
          <span>Image not available</span>
        </div>
      );
    }
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        onError={() => handleImageError(index, isMain)}
        loading="lazy"
      />
    );
  };

  if (loading) {
    return (
      <div className="adv-state">
        <Spin size="large" />
      </div>
    );
  }

  if (!ad) {
    return (
      <div className="adv-state">
        <Empty description="Listing not found" />
      </div>
    );
  }

  const phone = ad.contact?.phone?.replace(/\D/g, "");
  const audienceType = ad.visibility?.audienceType;
  const audienceTypeText = Array.isArray(audienceType)
    ? audienceType.join(", ")
    : audienceType;
  const audienceTags = ad.audienceTags || [];
  const vehicleType = ad.assetDetails?.vehicleType;
  const showVehicleType = ad.category === "vehicle" && vehicleType;
  const hasValidSize = ad.assetDetails?.size?.width && ad.assetDetails?.size?.height;
  const trafficLevel = ad.visibility?.trafficLevel || "—";

  return (
    <div className="adv-root">
      {/* Gallery */}
      <section className="adv-gallery">
        <div
          className="adv-gallery__main"
          onClick={() => {
            setPreviewIndex(0);
            setPreviewOpen(true);
          }}
        >
          {otherImages.length > 0 ? (
            renderImageWithFallback(
              otherImages[activeImage],
              ad.title,
              "adv-gallery__main-img",
              activeImage,
              true
            )
          ) : (
            images[0] ? (
              renderImageWithFallback(
                images[0],
                ad.title,
                "adv-gallery__main-img",
                0,
                true
              )
            ) : (
              <div className="adv-image-fallback">
                <span>Image not available</span>
              </div>
            )
          )}
          <div className="adv-gallery__overlay" />
          <button
            className="adv-gallery__badge"
            onClick={(e) => {
              e.stopPropagation();
              setPreviewIndex(0);
              setPreviewOpen(true);
            }}
            aria-label="View gallery"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
            {otherImages.length > 0 ? otherImages.length : 0} photo{otherImages.length !== 1 ? "s" : ""}
          </button>
        </div>

        {otherImages.length > 0 && (
          <div className="adv-gallery__strip">
            {otherImages.map((img, i) => (
              <button
                key={i}
                className={`adv-gallery__thumb${activeImage === i ? " adv-gallery__thumb--on" : ""
                  }`}
                onClick={() => setActiveImage(i)}
                aria-label={`View photo ${i + 1}`}
              >
                {renderImageWithFallback(
                  img,
                  `Thumbnail ${i + 1}`,
                  "adv-gallery__thumb-img",
                  i
                )}
                {i === 4 && otherImages.length > 5 && (
                  <span className="adv-gallery__more">
                    +{otherImages.length - 5}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Body */}
      <div className="adv-body">
        {/* Left Content */}
        <main className="adv-content">
          {/* Breadcrumb */}
          <nav className="adv-breadcrumb" aria-label="Breadcrumb">
            <span>Ads</span>
            <span className="adv-bc-sep">/</span>
            <span>{ad.location?.city || "Listings"}</span>
            <span className="adv-bc-sep">/</span>
            <span className="adv-bc-active">{ad.title}</span>
          </nav>

          {/* Header */}
          <div className="adv-header">
            <div className="adv-chips">
              <span className="adv-chip adv-chip--type">
                {ad.category || "Ad"}
              </span>
              <span
                className={`adv-chip adv-chip--status-${ad.status === "active" ? "active" : "inactive"
                  }`}
              >
                {ad.status}
              </span>
              {isAdmin && (
                <span
                  className={`adv-chip adv-chip--approval-${ad.approvalStatus}`}
                >
                  {ad.approvalStatus}
                </span>
              )}
              {ad.isFeatured && (
                <span className="adv-chip adv-chip--featured">⭐ Featured</span>
              )}
              {ad.pricing?.negotiable && (
                <span className="adv-chip adv-chip--nego">Negotiable</span>
              )}
            </div>

            <h1 className="adv-title">{ad.title}</h1>

            {[
              ad.location?.address,
              ad.location?.city,
              ad.location?.state,
            ].filter(Boolean).length > 0 && (
                <p className="adv-location">
                  <EnvironmentOutlined />
                  {[ad.location?.address, ad.location?.city, ad.location?.state]
                    .filter(Boolean)
                    .join(", ")}
                </p>
              )}
          </div>

          {/* Stats Bar */}
          <div className="adv-stats">
            {hasValidSize && (
              <>
                <div className="adv-stat">
                  <span className="adv-stat__icon">
                    <ColumnWidthOutlined />
                  </span>
                  <span className="adv-stat__val">
                    {ad.assetDetails.size.width}×{ad.assetDetails.size.height}
                  </span>
                  <span className="adv-stat__lbl">Size (px)</span>
                </div>
                <div className="adv-stat__div" />
              </>
            )}

            {showVehicleType && (
              <>
                <div className="adv-stat">
                  <span className="adv-stat__icon">
                    <CarOutlined />
                  </span>
                  <span className="adv-stat__val">{vehicleType}</span>
                  <span className="adv-stat__lbl">Vehicle Type</span>
                </div>
                <div className="adv-stat__div" />
              </>
            )}

            <div className="adv-stat">
              <span className="adv-stat__icon">
                <FireOutlined />
              </span>
              <span className="adv-stat__val">{trafficLevel}</span>
              <span className="adv-stat__lbl">Traffic Level</span>
            </div>
            <div className="adv-stat__div" />

            <div className="adv-stat">
              <span className="adv-stat__icon">
                <TeamOutlined />
              </span>
              <span className="adv-stat__val">{audienceTypeText || "—"}</span>
              <span className="adv-stat__lbl">Audience Type</span>
            </div>
            <div className="adv-stat__div" />

            <div className="adv-stat">
              <span className="adv-stat__icon">
                <BankOutlined />
              </span>
              <span className="adv-stat__val">
                {ad.pricing?.unit?.toUpperCase() || "—"}
              </span>
              <span className="adv-stat__lbl">Unit</span>
            </div>

            {isAdmin && (
              <>
                <div className="adv-stat__div" />
                <div className="adv-stat">
                  <span className="adv-stat__icon">
                    <EyeOutlined />
                  </span>
                  <span className="adv-stat__val">{ad.views || 0}</span>
                  <span className="adv-stat__lbl">Views</span>
                </div>
              </>
            )}
          </div>

          {/* Tabs */}
          <div className="adv-tabs">
            <div className="adv-tabs__nav" role="tablist">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  role="tab"
                  aria-selected={activeTab === tab}
                  className={`adv-tabs__btn${activeTab === tab ? " adv-tabs__btn--on" : ""
                    }`}
                  onClick={() => handleTabChange(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="adv-tabs__panel">
              {activeTab === "Details" && (
                <div className="adv-detail-grid">
                  <div className="adv-detail-card">
                    <h3 className="adv-detail-card__head">Ad Details</h3>
                    <table className="adv-detail-table">
                      <tbody>
                        {isAdmin && renderDetailRow("Listing ID", ad._id)}
                        {renderDetailRow("Category", ad.category)}
                        {renderDetailRow("Sub Type", ad.subType)}
                        {renderDetailRow(
                          "Placement Position",
                          ad.assetDetails?.placementPosition
                        )}
                        {hasValidSize &&
                          renderDetailRow(
                            "Size (Width x Height)",
                            `${ad.assetDetails.size.width}×${ad.assetDetails.size.height} px`
                          )}
                        {showVehicleType &&
                          renderDetailRow("Vehicle Type", vehicleType)}
                        {renderDetailRow(
                          "Max Ads Allowed",
                          ad.assetDetails?.maxAdsAllowed
                        )}
                        {renderDetailRow("Country", ad.location?.country)}
                        {renderDetailRow("Pincode", ad.location?.pincode)}
                        {isAdmin && renderDetailRow("Slug", ad.slug)}
                        {isAdmin &&
                          renderDetailRow(
                            "Latitude",
                            ad.location?.coordinates?.coordinates?.[1]
                          )}
                        {isAdmin &&
                          renderDetailRow(
                            "Longitude",
                            ad.location?.coordinates?.coordinates?.[0]
                          )}
                      </tbody>
                    </table>
                  </div>

                  <div className="adv-detail-card">
                    <h3 className="adv-detail-card__head">Pricing & Listing</h3>
                    <table className="adv-detail-table">
                      <tbody>
                        {renderDetailRow(
                          "Pricing Unit",
                          ad.pricing?.unit?.toUpperCase()
                        )}
                        {renderDetailRow(
                          "Price",
                          ad.pricing?.price
                            ? `₹ ${ad.pricing.price.toLocaleString("en-IN")}`
                            : null
                        )}
                        {renderDetailRow(
                          "Negotiable",
                          ad.pricing?.negotiable ? "Yes" : "No"
                        )}
                        {renderDetailRow("Status", ad.status)}
                        {isAdmin &&
                          renderDetailRow("Approval", ad.approvalStatus)}
                        {isAdmin && renderDetailRow("Views", ad.views || 0)}
                        {isAdmin &&
                          renderDetailRow(
                            "Expiry Date",
                            ad.expiryDate
                              ? new Date(ad.expiryDate).toLocaleDateString()
                              : null
                          )}
                        {renderDetailRow("Traffic Level", trafficLevel)}
                        {renderDetailRow("Audience Type", audienceTypeText)}
                        {renderDetailRow(
                          "Posted On",
                          ad.createdAt
                            ? new Date(ad.createdAt).toLocaleDateString()
                            : null
                        )}
                        {isAdmin &&
                          renderDetailRow(
                            "Updated On",
                            ad.updatedAt
                              ? new Date(ad.updatedAt).toLocaleDateString()
                              : null
                          )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === "Description" && (
                <div className="adv-desc-card">
                  <p className="adv-desc-text">
                    {ad.description || "No description available."}
                  </p>
                </div>
              )}

              {activeTab === "Location" && (
                <div className="adv-location-wrap">
                  <div className="adv-detail-grid">
                    <div className="adv-detail-card">
                      <h3 className="adv-detail-card__head">Address</h3>
                      <table className="adv-detail-table">
                        <tbody>
                          {renderDetailRow("Address", ad.location?.address)}
                          {renderDetailRow("Locality", ad.location?.locality)}
                          {renderDetailRow("City", ad.location?.city)}
                          {renderDetailRow("State", ad.location?.state)}
                          {renderDetailRow("Country", ad.location?.country)}
                          {renderDetailRow("Pincode", ad.location?.pincode)}
                        </tbody>
                      </table>
                    </div>
                    <div className="adv-detail-card">
                      <h3 className="adv-detail-card__head">Contact Info</h3>
                      <table className="adv-detail-table">
                        <tbody>
                          {renderDetailRow("Owner", ad.ownerId?.name)}
                          {isAdmin &&
                            renderDetailRow("Owner Email", ad.ownerId?.email)}
                          {renderDetailRow("Contact Name", ad.contact?.name)}
                          {renderDetailRow("Phone", ad.contact?.phone)}
                          {renderDetailRow("Contact Email", ad.contact?.email)}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {ad.location?.coordinates?.coordinates ? (
                    <div className="adv-map">
                      <iframe
                        width="100%"
                        height="360"
                        style={{ border: 0 }}
                        loading="lazy"
                        allowFullScreen
                        title="Location Map"
                        src={`https://www.google.com/maps?q=${ad.location.coordinates.coordinates[1]},${ad.location.coordinates.coordinates[0]}&z=15&output=embed`}
                      />
                    </div>
                  ) : (
                    <p className="adv-empty-msg">
                      No map coordinates available.
                    </p>
                  )}
                </div>
              )}

              {activeTab === "Audience" && (
                <div className="adv-audience-card">
                  {/* Audience Type section */}
                  {audienceType && (
                    <div className="adv-audience-type-section">
                      <h4 className="adv-audience-section-title">
                        Audience Type
                      </h4>
                      <div className="adv-audience-type-badge">
                        <TeamOutlined />
                        <span>{audienceTypeText}</span>
                      </div>
                    </div>
                  )}

                  {/* Audience Tags section */}
                  {audienceTags.length > 0 && (
                    <>
                      {audienceType && (
                        <div className="adv-audience-divider"></div>
                      )}
                      <h4 className="adv-audience-section-title">
                        Audience Tags
                      </h4>
                      <ul className="adv-audience-grid">
                        {audienceTags.map((tag, i) => (
                          <li key={i} className="adv-audience-item">
                            <span className="adv-audience__dot" />
                            {tag}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  {/* Empty state */}
                  {!audienceType && audienceTags.length === 0 && (
                    <p className="adv-empty-msg">
                      No audience information available for this ad.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Rejection Notice */}
          {ad.approvalStatus === "rejected" && ad.rejectionReason && (
            <div className="adv-rejection">
              <strong>Rejection Reason</strong>
              <p>{ad.rejectionReason}</p>
            </div>
          )}
        </main>

        {/* Sidebar */}
        <aside className="adv-sidebar">
          <div className="adv-sidebar__card">
            <div className="adv-sidebar__price">
              <span className="adv-sidebar__amt">
                ₹ {ad.pricing?.price?.toLocaleString("en-IN") || 0}
              </span>
              <span className="adv-sidebar__per">
                {getPriceLabel(ad.pricing?.unit)}
              </span>
            </div>

            <hr className="adv-sidebar__rule" />

            <div className="adv-sidebar__owner">
              <span className="adv-sidebar__avatar" aria-hidden="true">
                <UserOutlined />
              </span>
              <div className="adv-sidebar__owner-info">
                <span className="adv-sidebar__owner-name">
                  {ad.contact?.name || "Contact Owner"}
                </span>
                <span className="adv-sidebar__owner-role">Ad Owner</span>
              </div>
            </div>

            {(ad.contact?.phone || ad.contact?.email) && (
              <dl className="adv-sidebar__contact-list">
                {ad.contact?.phone && (
                  <>
                    <dt>Phone</dt>
                    <dd>{ad.contact.phone}</dd>
                  </>
                )}
                {ad.contact?.email && (
                  <>
                    <dt>Email</dt>
                    <dd className="adv-sidebar__email">{ad.contact.email}</dd>
                  </>
                )}
              </dl>
            )}

            {phone && (
              <div className="adv-sidebar__actions">
                <a href={`tel:${phone}`} className="adv-btn adv-btn--call">
                  <PhoneOutlined style={{ transform: "rotate(90deg)" }} />
                  Call Now
                </a>
                <a
                  href={`https://wa.me/${phone}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="adv-btn adv-btn--wa"
                >
                  <FaWhatsapp />
                  WhatsApp
                </a>
              </div>
            )}

            <hr className="adv-sidebar__rule" />

            <dl className="adv-sidebar__facts">
              {hasValidSize && (
                <div className="adv-sidebar__fact">
                  <dt>Size</dt>
                  <dd>
                    {ad.assetDetails.size.width}×{ad.assetDetails.size.height} px
                  </dd>
                </div>
              )}
              {showVehicleType && (
                <div className="adv-sidebar__fact">
                  <dt>Vehicle Type</dt>
                  <dd>{vehicleType}</dd>
                </div>
              )}
              <div className="adv-sidebar__fact">
                <dt>Traffic</dt>
                <dd>{trafficLevel}</dd>
              </div>
              <div className="adv-sidebar__fact">
                <dt>Audience Type</dt>
                <dd>{audienceTypeText || "—"}</dd>
              </div>
              <div className="adv-sidebar__fact">
                <dt>Posted</dt>
                <dd>
                  {ad.createdAt
                    ? new Date(ad.createdAt).toLocaleDateString()
                    : "—"}
                </dd>
              </div>
            </dl>

            {isAdmin && (
              <div className="adv-sidebar__admin">
                <span className="adv-sidebar__admin-label">
                  Listed by (Admin)
                </span>
                <span>{ad.ownerId?.name || "N/A"}</span>
                <span>{ad.ownerId?.email || "N/A"}</span>
              </div>
            )}
          </div>

          <p className="adv-sidebar__note">
            Listed on{" "}
            {ad.createdAt
              ? new Date(ad.createdAt).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })
              : "—"}
          </p>
        </aside>
      </div>

      {/* Mobile Sticky Footer */}
      {phone && (
        <div
          className="adv-mobile-bar"
          role="complementary"
          aria-label="Contact actions"
        >
          <div className="adv-mobile-bar__price">
            <span className="adv-mobile-bar__amt">
              ₹ {ad.pricing?.price?.toLocaleString("en-IN") || 0}
            </span>
            <span className="adv-mobile-bar__per">
              {getPriceLabel(ad.pricing?.unit)}
            </span>
          </div>
          <div className="adv-mobile-bar__btns">
            <a href={`tel:${phone}`} className="adv-mbtn adv-mbtn--call">
              <PhoneOutlined style={{ transform: "rotate(90deg)" }} /> Call
            </a>
            <a
              href={`https://wa.me/${phone}`}
              target="_blank"
              rel="noopener noreferrer"
              className="adv-mbtn adv-mbtn--wa"
            >
              <FaWhatsapp /> WhatsApp
            </a>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {previewOpen && (
        <div
          className="adv-lb"
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
          onClick={() => setPreviewOpen(false)}
        >
          <div
            className="adv-lb__blur"
            style={{ backgroundImage: `url(${otherImages[previewIndex] || images[0]})` }}
          />

          <button
            className="adv-lb__close"
            onClick={() => setPreviewOpen(false)}
            aria-label="Close preview"
          >
            ✕
          </button>

          <span className="adv-lb__counter">
            {previewIndex + 1} / {otherImages.length}
          </span>

          <div className="adv-lb__stage" onClick={(e) => e.stopPropagation()}>
            <button
              className="adv-lb__nav adv-lb__nav--prev"
              onClick={handlePrev}
              aria-label="Previous image"
            >
              ‹
            </button>
            {renderImageWithFallback(
              otherImages[previewIndex],
              `Preview ${previewIndex + 1}`,
              "adv-lb__img",
              previewIndex
            )}
            <button
              className="adv-lb__nav adv-lb__nav--next"
              onClick={handleNext}
              aria-label="Next image"
            >
              ›
            </button>
          </div>

          {otherImages.length > 1 && (
            <div
              className="adv-lb__strip"
              ref={stripRef}
              onClick={(e) => e.stopPropagation()}
            >
              {otherImages.map((img, i) => (
                <button
                  key={i}
                  className={`adv-lb__thumb${previewIndex === i ? " adv-lb__thumb--on" : ""
                    }`}
                  onClick={() => setPreviewIndex(i)}
                  aria-label={`Go to photo ${i + 1}`}
                >
                  {renderImageWithFallback(img, `Thumbnail ${i + 1}`, "", i)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AdsDetails;